import { useState, useEffect, useRef } from "react";
import {
  Bot,
  Zap,
  Clock,
  TrendingUp,
  Database,
  FileText,
  Target,
  Headphones,
  ShoppingCart,
  Calculator,
  Users,
  Package,
  Heart,
  Wrench,
  CalendarCheck,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Shield,
  Phone,
  Menu,
  X,
  Check,
  Play,
  MessageSquare,
  Cpu,
  Workflow,
  BarChart3,
  Lock,
  Rocket,
  Globe,
  Code,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "../assets/favicon.png";

// -------------------- LOGO --------------------

const Logo = ({ showName = true }) => (
  <div className="flex items-center gap-3">
    <img src={logo} alt="Wol Dynamics" className="h-10 w-auto" />
    {showName && (
      <span className="font-heading font-bold text-foreground text-xl">
        WOL <span className="text-primary">DYNAMICS</span>
      </span>
    )}
  </div>
);

// -------------------- ANIMATED COUNTER --------------------

const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let rafId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      // Ease-out cúbico: arranca rápido, desacelera al llegar al final
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// -------------------- TYPING EFFECT --------------------

const TypingEffect = ({ words, className }) => {
  const [currentWord, setCurrentWord] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const word = words[currentWord];
    let pauseTimeout;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        const next = word.substring(0, currentText.length + 1);
        setCurrentText(next);
        if (next === word) {
          // Palabra completa — pausar antes de borrar
          pauseTimeout = setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        const next = word.substring(0, currentText.length - 1);
        setCurrentText(next);
        if (next === "") {
          setIsDeleting(false);
          setCurrentWord((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => {
      clearTimeout(timeout);
      clearTimeout(pauseTimeout); // limpia el pause timeout si el componente se desmonta
    };
  }, [currentText, isDeleting, currentWord, words]);

  return (
    <span className={className}>
      {currentText}
      <span className="animate-pulse text-primary">|</span>
    </span>
  );
};

// -------------------- FLOATING ELEMENTS --------------------

const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-float" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-5s" }} />
    <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: "-2s" }} />

    <div className="absolute inset-0 opacity-20">
      <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
      <div className="absolute left-3/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent" />
    </div>

    <div className="absolute top-32 right-20 text-primary/20 animate-float">
      <Cpu className="w-8 h-8" />
    </div>
    <div className="absolute bottom-40 left-20 text-primary/20 animate-float" style={{ animationDelay: "-3s" }}>
      <Bot className="w-10 h-10" />
    </div>
    <div className="absolute top-1/2 right-32 text-primary/15 animate-float" style={{ animationDelay: "-7s" }}>
      <Workflow className="w-12 h-12" />
    </div>
  </div>
);

// -------------------- NAVIGATION --------------------

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    // passive: true para no bloquear el hilo principal en móviles
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Inicio" },
    { href: "#proceso", label: "Proceso" },
    { href: "#casos", label: "Casos de Uso" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-strong py-3" : "py-5"}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 group">
          <Logo showName={true} />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-primary transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <a href="/#contacto">
            <Button className="group bg-primary hover:bg-primary/90 text-black font-semibold px-6 relative overflow-hidden">
              <span className="relative z-10 flex items-center gap-2">
                Contactar
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
              <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </Button>
          </a>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden glass-strong mt-3 mx-6 rounded-lg p-6 animate-fade-in">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-foreground hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="/#contacto" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-primary hover:bg-primary/90 text-black font-semibold mt-2">
                Contactar
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// -------------------- HERO --------------------

const HeroSection = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
    <FloatingElements />
    <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />

    <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
      <div className="text-center">
        <div className="inline-flex items-center gap-3 glass px-5 py-2.5 rounded-full mb-10 border border-primary/20 animate-fade-in">
          <div className="relative">
            <Bot className="w-5 h-5 text-primary" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          </div>
          <span className="text-sm font-mono text-muted-foreground tracking-wider">
            AGENTES INTELIGENTES DE ÚLTIMA GENERACIÓN
          </span>
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-foreground mb-6 animate-slide-up"
          style={{ lineHeight: "1.2" }}
        >
          No es un chatbot.
          <span className="block mt-4 text-primary text-glow">Es tu nuevo empleado digital.</span>
        </h1>

        <p
          className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto mb-6 animate-slide-up font-light"
          style={{ animationDelay: "0.1s" }}
        >
          Automatiza{" "}
          <TypingEffect
            words={["ventas", "atención al cliente", "cobranzas", "agendamiento", "reportes"]}
            className="text-primary font-medium"
          />
        </p>

        <p
          className="text-lg text-muted-foreground/80 max-w-2xl mx-auto mb-12 animate-slide-up"
          style={{ animationDelay: "0.2s" }}
        >
          Agentes que piensan, aprenden y ejecutan. Trabajan 24/7 sin descanso, sin errores y sin excusas.
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-slide-up"
          style={{ animationDelay: "0.3s" }}
        >
          <a href="/#contacto">
            <Button size="lg" className="group bg-primary hover:bg-primary/90 text-black font-bold px-10 py-7 text-lg relative overflow-hidden">
              <span className="relative z-10 flex items-center gap-3">
                <Rocket className="w-5 h-5" />
                Quiero mi agente
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
              </span>
            </Button>
          </a>
          <a href="#demo">
            <Button size="lg" variant="outline" className="group border-2 border-primary/50 text-primary hover:bg-primary/10 px-10 py-7 text-lg">
              <Play className="w-5 h-5 mr-2" />
              Ver demostración
            </Button>
          </a>
        </div>

        <div className="flex flex-wrap justify-center gap-8 animate-slide-up" style={{ animationDelay: "0.4s" }}>
          {[
            { icon: Shield, text: "Datos 100% seguros" },
            { icon: Zap, text: "Implementación rápida" },
            { icon: Clock, text: "Soporte continuo" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-muted-foreground">
              <item.icon className="w-4 h-4 text-primary" />
              <span className="text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 rounded-full border-2 border-primary/30 flex items-start justify-center p-2">
        <div className="w-1 h-2 bg-primary rounded-full animate-pulse" />
      </div>
    </div>
  </section>
);

// -------------------- STATS --------------------

const StatsSection = () => {
  const stats = [
    { value: 24, suffix: "/7", label: "Disponibilidad", description: "Sin descansos ni vacaciones" },
    { value: 90, suffix: "%", label: "Ahorro de tiempo", description: "En tareas repetitivas" },
    { value: 3, suffix: "x", label: "Más productividad", description: "Comparado con métodos tradicionales" },
    { value: 100, suffix: "%", label: "Personalizado", description: "Adaptado a tu negocio" },
  ];

  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-primary text-glow-sm mb-2">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-lg font-semibold text-foreground mb-1">{stat.label}</div>
              <div className="text-sm text-muted-foreground">{stat.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- COMPARISON --------------------

const ComparisonSection = () => (
  <section className="relative py-24 overflow-hidden">
    <div className="relative z-10 max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <span className="text-primary font-mono text-sm tracking-wider">LA DIFERENCIA</span>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mt-4" style={{ lineHeight: "1.3" }}>
          Chatbot tradicional vs <span className="text-primary">Agente Inteligente</span>
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="glass p-8 rounded-2xl border border-red-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-red-500/10 text-red-400 px-4 py-1 text-sm font-mono rounded-bl-lg">
            LIMITADO
          </div>
          <h3 className="font-heading text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-red-400" />
            Chatbot Tradicional
          </h3>
          <ul className="space-y-4">
            {[
              "Respuestas predefinidas y rígidas",
              "No entiende contexto ni matices",
              "Solo responde, no ejecuta acciones",
              "Requiere actualización manual constante",
              "Frustra a los usuarios con loops",
              "No aprende de las interacciones",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-muted-foreground">
                <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="glass p-8 rounded-2xl border border-primary/30 relative overflow-hidden group hover:border-primary/50 transition-colors">
          <div className="absolute top-0 right-0 bg-primary/20 text-primary px-4 py-1 text-sm font-mono rounded-bl-lg">
            AVANZADO
          </div>
          <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl group-hover:bg-primary/20 transition-colors" />
          <h3 className="font-heading text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
            <Bot className="w-8 h-8 text-primary" />
            Agente Inteligente
          </h3>
          <ul className="space-y-4 relative z-10">
            {[
              "Entiende lenguaje natural y contexto",
              "Toma decisiones basadas en datos",
              "Ejecuta acciones: cobra, agenda, envía",
              "Aprende y mejora automáticamente",
              "Resuelve problemas complejos",
              "Se integra con tus sistemas existentes",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-foreground">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// -------------------- PROCESS --------------------

const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      icon: MessageSquare,
      title: "Auditoría gratuita",
      description: "Analizamos tus procesos actuales y detectamos oportunidades de automatización.",
    },
    {
      number: "02",
      icon: Code,
      title: "Diseño personalizado",
      description: "Creamos un agente a medida que entiende tu negocio y habla como vos querés.",
    },
    {
      number: "03",
      icon: Rocket,
      title: "Implementación",
      description: "Lo conectamos a tus canales: WhatsApp, web, email, y tus sistemas internos.",
    },
    {
      number: "04",
      icon: BarChart3,
      title: "Optimización continua",
      description: "Monitoreamos, ajustamos y mejoramos constantemente el rendimiento.",
    },
  ];

  return (
    <section id="proceso" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 gradient-radial" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-primary font-mono text-sm tracking-wider">PROCESO</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mt-4 mb-6" style={{ lineHeight: "1.3" }}>
            De la idea a la <span className="text-primary">automatización</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Un proceso simple y transparente. Sin sorpresas, sin letra chica.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent -translate-y-1/2" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="relative group">
                <div className="glass p-8 rounded-2xl h-full hover:border-primary/50 transition-all duration-500 hover:-translate-y-2">
                  <div className="absolute -top-4 left-8 bg-primary text-black font-heading font-bold px-3 py-1 text-sm rounded-full">
                    {step.number}
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>

                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-4 -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-primary/50" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// -------------------- FEATURES --------------------

const FeaturesSection = () => {
  const features = [
    {
      icon: Database,
      title: "Base de datos propias",
      description: "Tus datos son tuyos. Bases de datos seguras, privadas y encriptadas que solo vos controlás.",
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    {
      icon: FileText,
      title: "Reportes automáticos",
      description: "Dashboards en tiempo real e informes que se generan solos. Decisiones basadas en datos, no en intuición.",
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    {
      icon: Target,
      title: "Resultados medibles",
      description: "Cada acción se rastrea. Sabés exactamente qué funciona y qué ajustar desde el día uno.",
      gradient: "from-green-500/20 to-emerald-500/20",
    },
    {
      icon: Lock,
      title: "Seguridad total",
      description: "Encriptación de punta a punta, backups automáticos y cumplimiento de las mejores prácticas.",
      gradient: "from-orange-500/20 to-red-500/20",
    },
    {
      icon: Globe,
      title: "Integraciones ilimitadas",
      description: "WhatsApp, Mercado Pago, Google Calendar, tu ERP, tu CRM... Si tiene API, lo conectamos.",
      gradient: "from-cyan-500/20 to-blue-500/20",
    },
    {
      icon: Sparkles,
      title: "IA de última generación",
      description: "Modelos de lenguaje avanzados que entienden contexto, sarcasmo, y hasta errores de tipeo.",
      gradient: "from-violet-500/20 to-purple-500/20",
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-primary font-mono text-sm tracking-wider">CAPACIDADES</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mt-4 mb-6" style={{ lineHeight: "1.3" }}>
            Todo lo que necesitás, <span className="text-primary">nada que no</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group relative glass p-8 rounded-2xl hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- USE CASES --------------------

const UseCasesSection = () => {
  const [activeCase, setActiveCase] = useState(0);

  const useCases = [
    {
      icon: Headphones,
      title: "Atención al Cliente 24/7",
      description: "Respuestas instantáneas, derivación inteligente y resolución de problemas sin intervención humana.",
      benefits: ["Reducí tiempos de espera a 0", "Atendé en múltiples idiomas", "Escalá solo lo necesario"],
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: ShoppingCart,
      title: "Ventas Automatizadas",
      description: "Desde la consulta inicial hasta el cobro. El agente vende, negocia y cierra por vos.",
      benefits: ["Aumentá conversiones 3x", "Recuperá carritos abandonados", "Upselling automático"],
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: Calculator,
      title: "Presupuestador Inteligente",
      description: "Genera cotizaciones profesionales en segundos basándose en las necesidades del cliente.",
      benefits: ["PDFs personalizados", "Cálculos complejos al instante", "Seguimiento automático"],
      color: "from-purple-500 to-violet-500",
    },
    {
      icon: Users,
      title: "CRM y Gestión de Leads",
      description: "Captura, califica y nutre leads automáticamente. Cada prospecto recibe atención personalizada.",
      benefits: ["Lead scoring automático", "Nurturing personalizado", "Asignación inteligente"],
      color: "from-orange-500 to-red-500",
    },
    {
      icon: Package,
      title: "Gestión de Inventario",
      description: "Control de stock en tiempo real con predicciones de demanda y alertas automáticas.",
      benefits: ["Predicción de demanda", "Alertas de reposición", "Órdenes automáticas"],
      color: "from-pink-500 to-rose-500",
    },
    {
      icon: CalendarCheck,
      title: "Agendamiento Inteligente",
      description: "Sistema de citas que se adapta a tu disponibilidad y la de tus clientes automáticamente.",
      benefits: ["Recordatorios multicanal", "Reprogramación fácil", "Sincronización de calendarios"],
      color: "from-cyan-500 to-teal-500",
    },
  ];

  // Sacar el ícono activo fuera del JSX — sin IIFE
  const ActiveIcon = useCases[activeCase].icon;

  return (
    <section id="casos" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 gradient-radial" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-primary font-mono text-sm tracking-wider">CASOS DE USO</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mt-4 mb-6" style={{ lineHeight: "1.3" }}>
            Un agente para <span className="text-primary">cada necesidad</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Estos son los más comunes, pero si tenés algo específico en mente, lo hacemos realidad.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Selector */}
          <div className="lg:col-span-1 space-y-3">
            {useCases.map((useCase, index) => (
              <button
                key={useCase.title}
                onClick={() => setActiveCase(index)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-300 ${
                  activeCase === index ? "glass border-primary/50 bg-primary/5" : "hover:bg-white/5"
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  activeCase === index ? "bg-primary/20" : "bg-white/5"
                }`}>
                  <useCase.icon className={`w-6 h-6 transition-colors ${
                    activeCase === index ? "text-primary" : "text-muted-foreground"
                  }`} />
                </div>
                <span className={`font-medium transition-colors ${
                  activeCase === index ? "text-foreground" : "text-muted-foreground"
                }`}>
                  {useCase.title}
                </span>
                {activeCase === index && <ChevronRight className="w-5 h-5 text-primary ml-auto" />}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="lg:col-span-2">
            <div className="glass p-10 rounded-2xl h-full relative overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${useCases[activeCase].color} opacity-5`} />

              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${useCases[activeCase].color} flex items-center justify-center`}>
                    <ActiveIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-foreground">
                    {useCases[activeCase].title}
                  </h3>
                </div>

                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  {useCases[activeCase].description}
                </p>

                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground">Beneficios clave:</h4>
                  {useCases[activeCase].benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${useCases[activeCase].color} flex items-center justify-center`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>

                <a href="/#contacto" className="inline-block mt-10">
                  <Button className="group bg-primary hover:bg-primary/90 text-black font-semibold px-8">
                    <span className="flex items-center gap-2">
                      Quiero esto para mi negocio
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// -------------------- TRUST --------------------

const TrustSection = () => (
  <section className="relative py-24 overflow-hidden">
    <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
      <div className="glass-strong rounded-3xl p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />

        <div className="relative z-10">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-8">
            <Shield className="w-10 h-10 text-primary" />
          </div>

          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6" style={{ lineHeight: "1.3" }}>
            Tu tranquilidad es nuestra prioridad
          </h2>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Trabajamos con total transparencia: sin costos ocultos, sin contratos eternos, y con
            garantía de satisfacción.
          </p>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { icon: Lock, title: "Sin contratos", description: "Cancelá cuando quieras" },
              { icon: Shield, title: "Garantía total", description: "Si no funciona, no pagás" },
              { icon: MessageSquare, title: "Soporte directo", description: "Hablás conmigo, no con un bot" },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// -------------------- FAQ --------------------

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "¿Qué diferencia hay entre un chatbot y un agente inteligente?",
      answer: "Un chatbot responde con respuestas predefinidas basadas en palabras clave. Un agente inteligente entiende el contexto, aprende de las interacciones, toma decisiones y puede ejecutar acciones complejas como procesar pagos, actualizar bases de datos o generar documentos.",
    },
    {
      question: "¿Cuánto tiempo toma implementar una solución?",
      answer: "Depende de la complejidad. Una automatización básica puede estar funcionando en 1-2 semanas. Soluciones más complejas con múltiples integraciones pueden tomar 4-6 semanas. Siempre priorizamos tener algo funcionando rápido para que veas resultados.",
    },
    {
      question: "¿Mis datos están seguros?",
      answer: "Absolutamente. Utilizamos bases de datos privadas y encriptadas. Tus datos nunca se comparten con terceros y cumplimos con las mejores prácticas de seguridad. Vos sos el único dueño de tu información.",
    },
    {
      question: "¿Puedo integrar esto con mis sistemas actuales?",
      answer: "Sí. Nos especializamos en integrar con sistemas existentes: ERPs, CRMs, plataformas de e-commerce, sistemas de facturación, WhatsApp Business, y más. Si tiene una API, podemos conectarlo.",
    },
    {
      question: "¿Qué pasa si necesito cambios después de implementado?",
      answer: "Los agentes son flexibles y adaptables. Incluimos un período de ajustes post-implementación y ofrecemos planes de mantenimiento mensual para mejoras continuas y nuevas funcionalidades.",
    },
    {
      question: "¿Cómo mido el retorno de inversión?",
      answer: "Cada solución incluye métricas claras: tiempo ahorrado, consultas atendidas, ventas procesadas, etc. Desde el día uno vas a poder ver exactamente qué está generando tu inversión.",
    },
  ];

  return (
    <section id="faq" className="relative py-24 overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-primary font-mono text-sm tracking-wider">FAQ</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mt-4 mb-6" style={{ lineHeight: "1.3" }}>
            Preguntas <span className="text-primary">frecuentes</span>
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`glass rounded-2xl overflow-hidden transition-all duration-300 ${
                openIndex === index ? "border-primary/50" : ""
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-heading font-semibold text-foreground pr-4 text-lg">
                  {faq.question}
                </span>
                <div className={`w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  openIndex === index ? "bg-primary/20 rotate-180" : ""
                }`}>
                  <ChevronDown className="w-5 h-5 text-primary" />
                </div>
              </button>

              <div className={`overflow-hidden transition-all duration-500 ${
                openIndex === index ? "max-h-96" : "max-h-0"
              }`}>
                <p className="px-6 pb-6 text-muted-foreground leading-relaxed text-lg">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- CTA --------------------

const CTASection = () => (
  <section className="relative py-32 overflow-hidden">
    <FloatingElements />

    <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
      <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-8" style={{ lineHeight: "1.2" }}>
        ¿Listo para tener tu propio
        <span className="block text-primary text-glow mt-2">agente inteligente?</span>
      </h2>

      <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
        Agendá una auditoría gratuita y descubrí cómo automatizar tu negocio en tiempo récord.
      </p>

      <div className="flex flex-col sm:flex-row gap-6 justify-center">
        <a href="/#contacto">
          <Button size="lg" className="group bg-primary hover:bg-primary/90 text-black font-bold px-12 py-8 text-xl relative overflow-hidden">
            <span className="relative z-10 flex items-center gap-3">
              <Rocket className="w-6 h-6" />
              Solicitar Auditoría Gratis
              <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
            </span>
          </Button>
        </a>
      </div>

      <p className="mt-8 text-muted-foreground">
        <Phone className="w-4 h-4 inline mr-2" />
        O escribime directo al{" "}
        <a href="https://wa.me/5491135921999" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          WhatsApp
        </a>
      </p>
    </div>
  </section>
);

// -------------------- FOOTER --------------------

const Footer = () => (
  <footer className="relative py-12 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <Logo showName={true} />
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} Wol Dynamics. Todos los derechos reservados.
        </p>
        <div className="flex items-center gap-6">
          <a href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Inicio</a>
          <a href="/#contacto" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contacto</a>
        </div>
      </div>
    </div>
  </footer>
);

// -------------------- WHATSAPP --------------------

const WhatsAppButton = () => (
  <a
    href="https://wa.me/5491135921999"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-button animate-pulse-glow"
    aria-label="Contactar por WhatsApp"
  >
    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
);

// -------------------- PAGE --------------------

const SolucionesPage = () => (
  <div className="min-h-screen bg-[#030305]">
    <Navigation />
    <HeroSection />
    <StatsSection />
    <ComparisonSection />
    <ProcessSection />
    <FeaturesSection />
    <UseCasesSection />
    <TrustSection />
    <FAQSection />
    <CTASection />
    <Footer />
    <WhatsAppButton />
  </div>
);

export default SolucionesPage;